import { useCallback, useState } from 'react';
import { useApp } from '../context/AppContext';

const DEFAULT_FEATURE = 'use this feature';

// One shared pattern for every write-triggering action that's disabled in demo mode
// (Export, Upload, Approve/Submit, Generate report, download PDF, ...). The button/
// control stays visible and enabled — guard only intercepts the click, showing the
// sign-in-required modal instead of running the real action. userMode is read from
// AppContext (explicit, not inferred), so this never disagrees with what Layout.tsx/
// Account.tsx use to decide what to render.
//
// feature is passed per-call (guard(action, 'export to P6')), not fixed at render time —
// a screen with more than one guarded action (Report.tsx has Generate, Export, and PDF
// download) needs the modal text to match whichever one was actually clicked, not
// whichever action happened to be declared last.
export function useDemoGuard() {
  const { userMode } = useApp();
  const isDemoMode = userMode === 'demo';
  const [gate, setGate] = useState<{ open: boolean; feature: string }>({ open: false, feature: DEFAULT_FEATURE });

  const guard = useCallback(
    (action: () => void, feature: string = DEFAULT_FEATURE) => {
      if (isDemoMode) {
        setGate({ open: true, feature });
        return;
      }
      action();
    },
    [isDemoMode]
  );

  return {
    isDemoMode,
    gateOpen: gate.open,
    gateFeature: gate.feature,
    openGate: (feature: string = DEFAULT_FEATURE) => setGate({ open: true, feature }),
    closeGate: () => setGate((g) => ({ ...g, open: false })),
    guard,
  };
}
